import { useCallback, useEffect, useState } from "react";
import type { ModelPriceView } from "../../../src/admin/prices.ts";
import { fetchJson } from "../lib/fetchJson.ts";
import { day, Refusal } from "./bits.tsx";

// The Spend screen's price table: every row, current and historical, in
// dollars per million tokens; one form that adds a row or edits one; and the
// two bulk verbs, refreshing OpenRouter's prices and pricing the ledger rows
// that landed with no price. See ADR-0051 §4.

const PRICE_FIELDS = [
  { key: "inputMicrosPerMtok", label: "Input $/Mtok" },
  { key: "outputMicrosPerMtok", label: "Output $/Mtok" },
  { key: "cacheReadMicrosPerMtok", label: "Cache read $/Mtok" },
  { key: "cacheWriteMicrosPerMtok", label: "Cache write $/Mtok" },
] as const;

type PriceKey = (typeof PRICE_FIELDS)[number]["key"];

type Draft = { provider: string; model: string; effectiveFrom: string } & Record<PriceKey, string>;

// Up to four decimals, at least two: $0.075 stays $0.075, $3 reads $3.00.
function perMtok(micros: number): string {
  return `$${(micros / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

function blankDraft(): Draft {
  return {
    provider: "",
    model: "",
    effectiveFrom: todayIso(),
    inputMicrosPerMtok: "",
    outputMicrosPerMtok: "",
    cacheReadMicrosPerMtok: "",
    cacheWriteMicrosPerMtok: "",
  };
}

function draftOf(p: ModelPriceView): Draft {
  const dollars = (m: number) => String(m / 1_000_000);
  return {
    provider: p.provider,
    model: p.model,
    effectiveFrom: day(p.effectiveFrom),
    inputMicrosPerMtok: dollars(p.inputMicrosPerMtok),
    outputMicrosPerMtok: dollars(p.outputMicrosPerMtok),
    cacheReadMicrosPerMtok: dollars(p.cacheReadMicrosPerMtok),
    cacheWriteMicrosPerMtok: dollars(p.cacheWriteMicrosPerMtok),
  };
}

// The upsert's body, prices in micro-USD; a message instead when a price is
// not a number of dollars.
function bodyOf(d: Draft): Record<string, string | number> | string {
  const out: Record<string, string | number> = { provider: d.provider.trim(), model: d.model.trim(), effectiveFrom: d.effectiveFrom };
  for (const f of PRICE_FIELDS) {
    const v = d[f.key].trim();
    if (!/^\d+(\.\d+)?$/.test(v)) return `${f.label} must be a number of dollars, such as 3 or 0.30`;
    out[f.key] = Math.round(Number(v) * 1_000_000);
  }
  return out;
}

function stateOf(p: ModelPriceView): JSX.Element {
  if (p.current) return <span className="pill ok">current</span>;
  if (Date.parse(p.effectiveFrom) > Date.now()) return <span className="pill warn">scheduled</span>;
  return <span className="pill idle">superseded</span>;
}

/**
 * The price table panel: lists `/api/spend/prices`, adds or edits a row
 * through one form, refreshes OpenRouter's prices and prices the unpriced
 * ledger rows, saying what each verb changed. `onPriced` runs after rows
 * are priced so the screen's totals re-read.
 */
export function PricePanel({ onPriced }: { onPriced: () => void }): JSX.Element {
  const [prices, setPrices] = useState<ModelPriceView[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPrices((await fetchJson<{ prices: ModelPriceView[] }>("/api/spend/prices")).prices);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function verb<T>(run: () => Promise<T>, done: (body: T) => void): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      done(await run());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function save(d: Draft): void {
    const body = bodyOf(d);
    if (typeof body === "string") {
      setError(body);
      return;
    }
    void verb(
      () => fetchJson<{ prices: ModelPriceView[] }>("/api/spend/prices", { method: "PUT", body: JSON.stringify(body) }),
      (b) => {
        setPrices(b.prices);
        setDraft(null);
        setStatus(`Saved ${body.provider} · ${body.model} from ${body.effectiveFrom}.`);
      },
    );
  }

  const refresh = () =>
    verb(
      () =>
        fetchJson<{ refreshed: { changed: number; unchanged: number; skipped: number }; prices: ModelPriceView[] }>(
          "/api/spend/prices/refresh",
          { method: "POST" },
        ),
      (b) => {
        setPrices(b.prices);
        const r = b.refreshed;
        setStatus(`Refreshed OpenRouter prices: ${r.changed} changed, ${r.unchanged} unchanged, ${r.skipped} skipped.`);
      },
    );

  const priceUnpriced = () =>
    verb(
      () => fetchJson<{ priced: number }>("/api/spend/prices/price-unpriced", { method: "POST" }),
      (b) => {
        setStatus(`Priced ${b.priced} ${b.priced === 1 ? "row" : "rows"} that had no price.`);
        onPriced();
      },
    );

  return (
    <section className="panel" aria-label="Price table" id="prices">
      <div className="panel-head wrap">
        <h2>Price table</h2>
        <span className="meta">micro-USD per million tokens, shown in dollars</span>
        <div className="actions">
          <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void refresh()}>
            Refresh OpenRouter prices
          </button>
          <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void priceUnpriced()}>
            Price unpriced rows
          </button>
          <button
            type="button"
            className="btn primary sm"
            disabled={busy}
            onClick={() => {
              setError(null);
              setDraft(blankDraft());
            }}
          >
            Add price
          </button>
        </div>
      </div>
      {status ? (
        <div className="spend-status" role="status">
          {status}
        </div>
      ) : null}
      {draft ? (
        <PriceForm draft={draft} busy={busy} onChange={setDraft} onSave={save} onCancel={() => setDraft(null)} error={error} />
      ) : error ? (
        <div className="panel-body">
          <Refusal message={error} />
        </div>
      ) : null}
      <div className="panel-body flush scroll-x">
        {prices === null ? (
          <p className="panel-body" aria-live="polite">Loading…</p>
        ) : prices.length === 0 ? (
          <p className="empty">The price table is empty. Add a row, or refresh OpenRouter&apos;s prices.</p>
        ) : (
          <table className="grid spend">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Effective from</th>
                {PRICE_FIELDS.map((f) => (
                  <th key={f.key} className="num">
                    {f.label.replace(" $/Mtok", "")}
                  </th>
                ))}
                <th>State</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {prices.map((p) => (
                <tr key={`${p.provider} ${p.model} ${p.effectiveFrom}`}>
                  <td className="mono">{p.provider}</td>
                  <td className="mono">{p.model}</td>
                  <td className="nowrap">{day(p.effectiveFrom)}</td>
                  {PRICE_FIELDS.map((f) => (
                    <td key={f.key} className="num">
                      {perMtok(p[f.key])}
                    </td>
                  ))}
                  <td>{stateOf(p)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn ghost xs"
                      disabled={busy}
                      onClick={() => {
                        setError(null);
                        setDraft(draftOf(p));
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function PriceForm({
  draft,
  busy,
  error,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Draft;
  busy: boolean;
  error: string | null;
  onChange: (d: Draft) => void;
  onSave: (d: Draft) => void;
  onCancel: () => void;
}): JSX.Element {
  const text = (key: keyof Draft, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div className="field">
      <label htmlFor={`price-${key}`}>{label}</label>
      <input
        id={`price-${key}`}
        type="text"
        autoComplete="off"
        value={draft[key]}
        disabled={busy}
        onChange={(e) => onChange({ ...draft, [key]: e.target.value })}
        {...extra}
      />
    </div>
  );
  return (
    <form
      className="price-form"
      aria-label="price row"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      {text("provider", "Provider")}
      {text("model", "Model")}
      <div className="field">
        <label htmlFor="price-effectiveFrom">Effective from</label>
        <input
          id="price-effectiveFrom"
          type="date"
          value={draft.effectiveFrom}
          disabled={busy}
          onChange={(e) => onChange({ ...draft, effectiveFrom: e.target.value })}
        />
      </div>
      {PRICE_FIELDS.map((f) => (
        <div key={f.key} style={{ display: "contents" }}>
          {text(f.key, f.label, { inputMode: "decimal", placeholder: "0.00" })}
        </div>
      ))}
      <div className="btn-row">
        <button type="submit" className="btn primary sm" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn ghost sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
      {error ? (
        <div style={{ gridColumn: "1 / -1" }}>
          <Refusal message={error} />
        </div>
      ) : null}
    </form>
  );
}
